import { useMemo, useState } from "react";
import DataGrid from "react-data-grid";
import AutoSizer from "react-virtualized-auto-sizer";

import { Error } from "@material-ui/icons";
import classnames from "classnames";
import { EvaChart, RigFracTimeScale } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import { withRigFracBins } from "entities/charts/eva/requests/rigFracBins";
import { DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracRecentActivity";
import styled from "styled-components/macro";
import { RigFracChartRequestType } from "types";
import { ExportFormat, exportFile } from "utils/exportUtils";

import { useChartEntities } from "hooks/charts";
import { useRigFracFocusBins } from "hooks/rigfrac/useRigFracFocusBins";
import useUser from "hooks/useUser";

import useExportRigFracActivityMutation from "api/useExportRigFracActivityMutation";

import getRigFracActivityDataTable from "components/charts/helpers/getRigFracActivityDataTable";
import getRigFracRecentActivityDataTable from "components/charts/helpers/getRigFracRecentActivityDataTable";

import { ChartDataTableContextMenu } from "../toggles/data-table/ChartDataTableContextMenu";
import { CopyTableButton } from "../toggles/data-table/CopyTableButton";
import { ExportButton } from "../toggles/data-table/ExportButton";
import { useChartDataTableInteractions } from "../toggles/data-table/hooks/useChartDataTableInteractions";
import { createChartDataTableHeaderRenderer } from "../toggles/data-table/utils/createChartDataTableHeaderRenderer";

const MIN_COLUMN_WIDTH = 150;

/**
 * Data table for the Rig/Frac Activity chart (EVA-76 US4). A focused sibling of
 * the wells ChartDataTable: uses getRigFracActivityDataTable for the table shape
 * and useExportRigFracActivityMutation (the /api/v1/rigFracChart/export route)
 * for its inline export. Overlays the chart when features.datatable.active.
 *
 * Copy support (cell click, right-click cell/row/column, copy-whole-table) reuses
 * the same shared generic hooks/components as the wells table.
 */
function RigFracDataTable() {
  const { id } = useChartsContext();
  const { apiResponse, request, chartType } = useChartEntities(id);
  const { isReadonly, hasTrialAccount } = useUser();
  const [exportError, setExportError] = useState<Maybe<string>>();

  const isRecent = chartType === EvaChart.RigFracRecentActivity;
  const selections = request as unknown as Maybe<Partial<RigFracChartRequestType>>;
  const focusSelection = useRigFracFocusBins(
    selections?.groupBy ?? DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.groupBy,
    true
  );

  // The time scale drives the Activity table's descriptive bucket-column header
  // (e.g. "Year-QTR"); Recent Activity has no time scale (single 7-day window),
  // so this is undefined there and the header falls back to the backend title.
  const timeScale = (request as Maybe<{ timeScale?: RigFracTimeScale }>)?.timeScale;

  // Route to the right table shape: Activity Over Time = one row per bucket,
  // one column per series; Recent Activity = one row per group (group, value).
  const { columns, rows } = useMemo(() => {
    if (!apiResponse) return { columns: [], rows: [] };
    return isRecent
      ? getRigFracRecentActivityDataTable(apiResponse)
      : getRigFracActivityDataTable(apiResponse, timeScale);
  }, [apiResponse, isRecent, timeScale]);

  const {
    highlightedCell,
    contextMenu,
    activeColumnCopyAlerts,
    handleCellClick,
    handleContextMenuAction,
    copyColumnData,
    copyTableData,
    setContextMenu
  } = useChartDataTableInteractions({ rows, columns, hasTrialAccount, isReadonly });

  const handleHeaderContextMenu = (
    event: React.MouseEvent,
    columnName: string,
    columnKey: string
  ) => {
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      row: {},
      columnKey,
      columnName
    });
  };

  const title = apiResponse?.title ?? (isRecent ? "Recent Activity" : "Activity vs Time");
  const canExport = !isReadonly && !hasTrialAccount;

  const exportMutation = useExportRigFracActivityMutation(
    (response, params) => {
      setExportError(null);
      exportFile(response.data, params.filename);
    },
    // Surface the mutation's specific message when it has one ("No data to
    // export" for the JSON-bodied no-data response) over the generic fallback.
    (error) => setExportError(error?.message || "Failed to export chart data")
  );

  const handleExport = (format: ExportFormat) => {
    // The entity request holds the lean rig/frac request (set by the controller);
    // default it — per the active chart type — for an export before the first
    // response lands. The chartType discriminator is FORCED from the live chart
    // type: the persisted request's chartType lags a chart-type switch (it is
    // only rewritten when the next response lands), so trusting it could export
    // the previous chart's table shape.
    const base = isRecent
      ? DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST
      : DEFAULT_RIG_FRAC_ACTIVITY_REQUEST;
    // The bin fields come from the current focus and Max Bins for the same
    // reason: the entity holds the last response's echo, which lags both.
    const leanRequest = withRigFracBins(
      {
        ...base,
        ...selections,
        chartType: isRecent ? "RigFracRecentActivity" : "RigFracActivity"
      } as RigFracChartRequestType,
      focusSelection,
      selections?.maxBins
    );
    exportMutation.mutate({
      request: leanRequest,
      format,
      filename: `EVA ${title}.${format}`
    });
  };

  return (
    <Wrapper data-testid="rig-frac-data-table">
      <ChartTitle>{title}</ChartTitle>
      <DataTableWrapper>
        <div style={{ height: "100%" }}>
          <AutoSizer>
            {({ width, height }) => (
              <StyledGrid
                className={classnames("rdg-light")}
                columns={columns.map((column) => ({
                  ...column,
                  resizable: true,
                  minWidth: MIN_COLUMN_WIDTH,
                  width: Math.max(
                    MIN_COLUMN_WIDTH,
                    Number(width) / (columns.length || 1)
                  ),
                  headerRenderer: createChartDataTableHeaderRenderer({
                    hasTrialAccount,
                    isReadonly,
                    activeColumnCopyAlerts,
                    onColumnCopy: copyColumnData,
                    onHeaderContextMenu: handleHeaderContextMenu
                  }),
                  formatter: (props) => {
                    const cellValue = props.row[props.column.key];
                    return (
                      <div
                        style={{ width: "100%", height: "100%" }}
                        onContextMenu={(e) => {
                          if (hasTrialAccount || isReadonly) return;
                          e.preventDefault();
                          e.stopPropagation();
                          setContextMenu({
                            visible: true,
                            x: e.clientX,
                            y: e.clientY,
                            row: props.row,
                            cellValue,
                            columnKey: props.column.key
                          });
                        }}>
                        {cellValue}
                      </div>
                    );
                  },
                  cellClass: (_row: Record<string, unknown>, rowIdx: number) =>
                    highlightedCell &&
                    highlightedCell.rowIndex === rowIdx &&
                    highlightedCell.columnKey === column.key
                      ? "cell-highlighted"
                      : undefined
                }))}
                rows={rows}
                onRowClick={handleCellClick}
                style={{ width: `${Number(width)}px`, height: `${Number(height)}px` }}
              />
            )}
          </AutoSizer>
        </div>
      </DataTableWrapper>
      {canExport && (
        <DataTableActionsWrapper>
          {exportError && (
            <ErrorContainer>
              <Error style={{ top: 3 }} /> {exportError}
            </ErrorContainer>
          )}
          <ExportButton exporting={exportMutation.isLoading} onExport={handleExport} />
          <CopyTableButton onCopy={copyTableData} isDisabled={!canExport} />
        </DataTableActionsWrapper>
      )}

      {contextMenu && (
        <ChartDataTableContextMenu
          contextMenu={contextMenu}
          onAction={handleContextMenuAction}
        />
      )}
    </Wrapper>
  );
}

export default RigFracDataTable;

const Wrapper = styled.div`
  position: absolute;
  display: flex;
  gap: 5px;
  flex-direction: column;
  width: 100%;
  height: 100%;
  z-index: 9;
  background-color: #fff;
  padding: 10px;
`;

const ChartTitle = styled.div`
  flex-grow: 0;
  text-transform: uppercase;
  font-weight: 600;
`;

const DataTableWrapper = styled.div`
  flex-grow: 1;
  overflow-x: auto;
`;

const DataTableActionsWrapper = styled.div`
  flex-grow: 0;
  display: flex;
  align-items: center;
  text-align: right;
  justify-content: flex-end;
`;

// Cell hover + copy-highlight pulse, mirroring the wells ChartDataTable.
const StyledGrid = styled(DataGrid)`
  .rdg-cell {
    &:hover {
      background-color: rgba(220, 233, 246, 1);
    }
  }

  .cell-highlighted {
    background-color: #e6fffb !important;
    animation: cellCopyPulse 1s ease-out;
  }

  @keyframes cellCopyPulse {
    0% {
      background-color: #52c41a;
    }
    50% {
      background-color: #b7eb8f;
    }
    100% {
      background-color: #e6fffb;
    }
  }
`;

const ErrorContainer = styled.div`
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
`;
